import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

type ThemeLike = unknown;

type Column = { header: string; key: string };
type NormalizedTable = { columns: Column[]; rows: unknown[] };
type TableRowObject = Record<string, unknown>;

type ColumnsRowsShape = { columns: unknown; rows: unknown };
type HeadersShape = { headers: unknown; data?: unknown; rows?: unknown };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasColumnsRows(value: unknown): value is Record<string, unknown> & ColumnsRowsShape {
  return isRecord(value) && 'columns' in value && 'rows' in value;
}

function hasHeaders(value: unknown): value is Record<string, unknown> & HeadersShape {
  return isRecord(value) && 'headers' in value;
}

// Normalizador: intenta convertir "table" a columnas + filas
function normalizeTable(table: unknown): NormalizedTable {
  // Caso 1: array de objetos
  if (Array.isArray(table) && table.length > 0 && isRecord(table[0])) {
    const keys = Object.keys(table[0]);
    return {
      columns: keys.map(k => ({ header: k, key: k })),
      rows: table,
    };
  }

  // Caso 2: { columns: [...], rows: [...] }
  if (hasColumnsRows(table) && Array.isArray(table.columns) && Array.isArray(table.rows)) {
    // ✅ prefer-destructuring
    const { columns: rawColumns, rows } = table;

    const columns = rawColumns
      .filter(isRecord)
      .map(c => {
        const header =
          (c.header as string | undefined) ??
          (c.title as string | undefined) ??
          (c.label as string | undefined) ??
          (c.key as string | undefined) ??
          '';

        const key =
          (c.key as string | undefined) ??
          (c.field as string | undefined) ??
          (c.id as string | undefined) ??
          (c.header as string | undefined) ??
          header;

        return { header, key };
      })
      .filter(c => c.header && c.key);

    return { columns, rows: rows as unknown[] };
  }

  // Caso 3: { headers: [...], data: [...] } o { headers, rows }
  if (hasHeaders(table) && Array.isArray(table.headers)) {
    // ✅ prefer-destructuring
    const { headers, data, rows } = table;

    const maybeRows = data ?? rows;
    const headerStrings = (headers as unknown[]).filter((h): h is string => typeof h === 'string');

    return {
      columns: headerStrings.map(h => ({ header: h, key: h })),
      rows: Array.isArray(maybeRows) ? maybeRows : [],
    };
  }

  // Fallback
  return { columns: [], rows: [] };
}

export const TablesExportXlsx = (fileName: string) => async (table: unknown, theme: ThemeLike) => {
  // No usamos theme en XLSX (evitar no-void y no-underscore-dangle)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const unusedTheme = theme;

  const normalized = normalizeTable(table);
  const { columns, rows } = normalized;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('Report');

  sheet.columns = columns.map(c => ({
    header: c.header,
    key: c.key,
    width: Math.max(12, String(c.header).length + 4),
  }));

  rows.forEach(r => {
    if (Array.isArray(r)) {
      const obj: TableRowObject = {};
      columns.forEach((c, i) => {
        obj[c.key] = r[i];
      });
      sheet.addRow(obj);
      return;
    }

    if (isRecord(r)) {
      sheet.addRow(r);
      return;
    }

    sheet.addRow({ value: r } as Record<string, unknown>);
  });

  const headerRow = sheet.getRow(1);
  headerRow.font = { bold: true };
  sheet.views = [{ state: 'frozen', ySplit: 1 }];

  if (columns.length) {
    sheet.autoFilter = {
      from: { row: 1, column: 1 },
      to: { row: 1, column: columns.length },
    };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  saveAs(blob, `${fileName}.xlsx`);
};
